// /////////////////////////////////////////////////////////////
// HOME デザイン案1 オービットメニュー
//
// 中央の Hozenall ロゴにマウスを乗せると、ロゴを中心とした円周上へ
// メニュー (ユーザーが権限を持つ親メニュー) が放射状に展開する。
// 子メニューを持つ項目にホバーすると、その項目の外側へ子メニューのカードが開く。
// ステージ (展開領域) からマウスが離れると少し待ってから収束する。
// タッチ操作向けにロゴのクリックでも開閉できる。
// /////////////////////////////////////////////////////////////
import React, { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Paper, Typography } from '@mui/material';
import { amber, blue, blueGrey, deepPurple, teal } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import { MENU_CATEGORIES } from '../../constants/masterData';
import { APP_BACKGROUND_COLOR } from '../../constants/colors';
import { NavigationMenuType } from '../../firebase/PermissionProvider';
import { isExternalMenuPath, openExternalMenuPath } from '../../utils/menuLink';
import logoImage from '../../assets/images/hozenall_logo.png';
import type { CenterEffectMode, SceneTarget } from './ThreeCenterScene';

// 中央の Three.js 演出 (案3・案4)。three.js 本体は選択時にだけ読み込む
const ThreeCenterScene = lazy(() => import('./ThreeCenterScene'));

// 中央コア (ロゴを載せる白い円) の直径 (px)。ロゴは横長のため円内に幅を合わせて置く
const LOGO_SIZE = 280;
// ブランドカラー (ロゴの緑)
const BRAND_GREEN = '#327d39';
// メニュー項目の丸アイコンの直径 (px)
const ITEM_SIZE = 60;
// 円周上で 1 項目が占める弧長の目安 (px)。ラベル同士が重ならない間隔
const ITEM_ARC_SPACING = 96;
// 最小・既定の軌道半径 (px)
const MIN_ORBIT_RADIUS = 240;
// ステージ外周に残す余白 (px)。子メニューカードがはみ出しにくいよう確保
const STAGE_EDGE_MARGIN = 150;
// 軌道リングの外へ出てから閉じるまでの猶予 (ms)
const CLOSE_DELAY_MS = 350;
// 「リングの外」と判定するまでの余白 (px)。リング上の項目の外側半分とラベル分を含める
const ORBIT_EXIT_MARGIN = ITEM_SIZE / 2 + 36;

// カテゴリ (sort_no の整数部) ごとのアクセント色
const CATEGORY_COLORS: Record<string, string> = {
  '2': teal[600],
  '3': blue[600],
  '6': amber[800],
  '8': deepPurple[400],
  '9': blueGrey[500],
};
const DEFAULT_CATEGORY_COLOR = blue[400];

export interface OrbitMenuItem {
  menu: NavigationMenuType;
  children: NavigationMenuType[];
}

interface OrbitMenuHomeProps {
  items: OrbitMenuItem[];
  greeting?: string;
  // 円周上の項目がカテゴリ自体の場合は凡例が重複するため非表示にできる
  showLegend?: boolean;
  // 中央コンテンツの演出。未指定は HTML のロゴ (白い円 + ロゴ画像)
  centerEffect?: CenterEffectMode;
}

// アイコン名 (Firestore の icon フィールド) から MUI アイコンを描画する
const MenuIcon: React.FC<{ iconName?: string; fontSize?: number }> = ({ iconName, fontSize = 26 }) => {
  const SelectedIcon = (Icons[(iconName || 'HelpOutline') as keyof typeof Icons] ?? Icons.HelpOutline) as React.ElementType;
  return <SelectedIcon sx={{ fontSize }} />;
};

const categoryColorOf = (menu: NavigationMenuType) =>
  CATEGORY_COLORS[Math.floor(menu.sort_no).toString()] ?? DEFAULT_CATEGORY_COLOR;

export default function OrbitMenuHome({ items, greeting, showLegend = true, centerEffect }: OrbitMenuHomeProps) {
  // OS で「視差効果を減らす」を選んでいる利用者には動きの多い演出を出さず、通常のロゴにする
  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    [],
  );
  const effect = prefersReducedMotion ? undefined : centerEffect;
  const navigate = useNavigate();
  // 外部 URL のメニューは新しいタブで開き、それ以外は画面内遷移する
  const goTo = useCallback((path: string) => {
    if (isExternalMenuPath(path)) {
      openExternalMenuPath(path);
    } else {
      navigate(path);
    }
  }, [navigate]);
  const stageRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 子メニューカードはリングの外へはみ出すため、カード上にいる間は閉じない
  const overFlyoutRef = useRef(false);

  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // ステージの実寸を監視し、軌道半径の上限計算に使う
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setStageSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
      setActiveIndex(null);
    }, CLOSE_DELAY_MS);
  }, [cancelClose]);

  // カードがカーソル下で消えると mouseleave が発火しないため、切替・収束時に解除する
  useEffect(() => {
    overFlyoutRef.current = false;
  }, [activeIndex, open]);

  // 項目数から必要な半径を求め、ステージに収まる範囲へ丸める
  const radius = useMemo(() => {
    const needed = (items.length * ITEM_ARC_SPACING) / (2 * Math.PI);
    const maxByStage = Math.min(stageSize.width, stageSize.height) / 2 - STAGE_EDGE_MARGIN;
    const upper = Math.max(MIN_ORBIT_RADIUS, maxByStage);
    return Math.min(Math.max(needed, MIN_ORBIT_RADIUS), upper);
  }, [items.length, stageSize]);

  // 12 時の位置から時計回りに等間隔で配置
  const positions = useMemo(
    () =>
      items.map((_, i) => {
        const angle = -Math.PI / 2 + (2 * Math.PI * i) / Math.max(items.length, 1);
        return { angle, x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
      }),
    [items, radius],
  );

  // 展開中、カーソルが軌道リング (+余白) の外へ出たら閉じる。内側へ戻れば取り消す
  const handleStageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!open || !stageRef.current) return;
    if (overFlyoutRef.current) {
      cancelClose();
      return;
    }
    const rect = stageRef.current.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width / 2);
    const dy = e.clientY - (rect.top + rect.height / 2);
    const outside = Math.hypot(dx, dy) > radius + ORBIT_EXIT_MARGIN;
    if (!outside) {
      cancelClose();
    } else if (!closeTimerRef.current) {
      // 外側を動き続けてもタイマーを延長しないよう、未予約のときだけ予約する
      scheduleClose();
    }
  };

  const handleItemClick = (item: OrbitMenuItem, index: number) => {
    if (item.menu.path) {
      goTo(item.menu.path);
      return;
    }
    // 遷移先を持たない親項目はクリックで子メニューを開閉 (タッチ操作向け)
    if (item.children.length > 0) {
      setActiveIndex((prev) => (prev === index ? null : index));
    }
  };

  // Three.js 演出へ渡すメニュー項目の位置と色
  const sceneTargets = useMemo<SceneTarget[]>(
    () => positions.map((pos, i) => ({ x: pos.x, y: pos.y, color: categoryColorOf(items[i].menu) })),
    [positions, items],
  );

  const activeItem = activeIndex !== null ? items[activeIndex] : null;
  const activePos = activeIndex !== null ? positions[activeIndex] : null;

  return (
    <Box
      ref={stageRef}
      onMouseMove={handleStageMouseMove}
      onMouseLeave={scheduleClose}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 520,
        overflow: 'hidden',
        // 背景はアプリ全体の背景色に揃える
        bgcolor: APP_BACKGROUND_COLOR,
      }}
    >
      {effect && (
        <Suspense fallback={null}>
          <ThreeCenterScene
            mode={effect}
            open={open}
            activeIndex={activeIndex}
            targets={sceneTargets}
            logoUrl={logoImage}
            itemSize={ITEM_SIZE}
          />
        </Suspense>
      )}

      {/* 中心原点 (ここを基準に全要素を translate する) */}
      <Box sx={{ position: 'absolute', left: '50%', top: '50%', width: 0, height: 0 }}>
        {/* 軌道リング */}
        <AnimatePresence>
          {open && (
            <motion.div
              key="orbit-ring"
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.4 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                left: -radius,
                top: -radius,
                width: radius * 2,
                height: radius * 2,
                borderRadius: '50%',
                border: '2px dashed rgba(50, 125, 57, 0.25)',
                pointerEvents: 'none',
              }}
            />
          )}
        </AnimatePresence>

        {/* 親メニュー (円周上に展開) */}
        {items.map((item, i) => {
          const pos = positions[i];
          const color = categoryColorOf(item.menu);
          const isActive = activeIndex === i;
          return (
            <motion.div
              key={item.menu.id || `orbit-${i}`}
              initial={false}
              animate={
                open
                  ? { x: pos.x, y: pos.y, opacity: 1, scale: 1 }
                  : { x: 0, y: 0, opacity: 0, scale: 0.3 }
              }
              transition={{
                type: 'spring',
                stiffness: 260,
                damping: 22,
                // 展開時は時計回りに順番に、収束時は一斉に戻す
                delay: open ? i * 0.025 : 0,
              }}
              style={{
                position: 'absolute',
                left: -ITEM_SIZE / 2,
                top: -ITEM_SIZE / 2,
                width: ITEM_SIZE,
                zIndex: isActive ? 3 : 2,
                pointerEvents: open ? 'auto' : 'none',
              }}
            >
              <Box
                role="button"
                tabIndex={open ? 0 : -1}
                aria-label={item.menu.label}
                onMouseEnter={() => setActiveIndex(item.children.length > 0 ? i : null)}
                onClick={() => handleItemClick(item, i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleItemClick(item, i);
                  }
                }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  outline: 'none',
                  '&:hover .orbit-bubble, &:focus-visible .orbit-bubble': {
                    transform: 'scale(1.12)',
                    boxShadow: `0 6px 18px ${color}55`,
                    bgcolor: color,
                    color: '#fff',
                  },
                }}
              >
                <Box
                  className="orbit-bubble"
                  sx={{
                    width: ITEM_SIZE,
                    height: ITEM_SIZE,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isActive ? color : '#fff',
                    color: isActive ? '#fff' : color,
                    border: `2px solid ${color}`,
                    boxShadow: '0 3px 10px rgba(0,0,0,0.12)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <MenuIcon iconName={item.menu.icon} />
                </Box>
                <Typography
                  sx={{
                    mt: 0.75,
                    // 短い名前は 1 行、長い名前も末尾の › が改行されにくいよう少し広めに取る
                    width: 'max-content',
                    maxWidth: ITEM_ARC_SPACING + 36,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    lineHeight: 1.25,
                    textAlign: 'center',
                    color: 'text.primary',
                    textShadow: `0 0 4px ${APP_BACKGROUND_COLOR}, 0 0 4px ${APP_BACKGROUND_COLOR}`,
                    wordBreak: 'keep-all',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {item.menu.label || '無題の項目'}
                  {item.children.length > 0 && (
                    <Box component="span" sx={{ color, ml: 0.25 }}>›</Box>
                  )}
                </Typography>
              </Box>
            </motion.div>
          );
        })}

        {/* 子メニューカード (アクティブな親の外側へ開く) */}
        <AnimatePresence>
          {open && activeItem && activePos && activeItem.children.length > 0 && (
            <ChildFlyout
              key={activeItem.menu.id || `flyout-${activeIndex}`}
              item={activeItem}
              angle={activePos.angle}
              radius={radius}
              onNavigate={goTo}
              boundsRef={stageRef}
              onHoverChange={(hovering) => {
                overFlyoutRef.current = hovering;
                if (hovering) cancelClose();
              }}
            />
          )}
        </AnimatePresence>

        {/* 中央ロゴ */}
        <motion.div
          onMouseEnter={() => {
            cancelClose();
            setOpen(true);
            setActiveIndex(null);
          }}
          onClick={() => {
            setOpen((prev) => !prev);
            setActiveIndex(null);
          }}
          // Three.js 演出時は描画をキャンバスに任せ、ここはホバー判定の透明な領域だけにする
          role={effect ? 'img' : undefined}
          aria-label={effect ? 'Hozenall' : undefined}
          animate={effect ? undefined : open ? { scale: 0.8 } : { scale: [1, 1.03, 1] }}
          transition={
            open
              ? { type: 'spring', stiffness: 260, damping: 20 }
              : { duration: 3.2, repeat: Infinity, ease: 'easeInOut' }
          }
          whileHover={effect ? undefined : { scale: open ? 0.84 : 1.05 }}
          style={{
            borderRadius: '50%',
            position: 'absolute',
            left: -LOGO_SIZE / 2,
            top: -LOGO_SIZE / 2,
            width: LOGO_SIZE,
            height: LOGO_SIZE,
            zIndex: 4,
            cursor: 'pointer',
          }}
        >
          {!effect && (
          <>
          {/* ロゴ背面のグロー */}
          <Box
            sx={{
              position: 'absolute',
              inset: -18,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(76,160,84,0.30) 0%, rgba(76,160,84,0) 70%)',
              opacity: open ? 1 : 0.6,
              transition: 'opacity 0.3s ease',
              pointerEvents: 'none',
            }}
          />
          {/* 白い円形コア */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: '#fff',
              border: `2px solid ${open ? BRAND_GREEN : 'rgba(50,125,57,0.35)'}`,
              boxShadow: open ? '0 8px 28px rgba(50,125,57,0.25)' : '0 4px 16px rgba(0,0,0,0.10)',
              transition: 'all 0.3s ease',
            }}
          />
          <Box
            component="img"
            src={logoImage}
            alt="Hozenall"
            draggable={false}
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '80%',
              transform: 'translate(-50%, -50%)',
              userSelect: 'none',
            }}
          />
          </>
          )}
        </motion.div>

        {/* 閉じている時の案内 */}
        <AnimatePresence>
          {!open && (
            <motion.div
              key="orbit-hint"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.25 }}
              style={{
                position: 'absolute',
                left: -160,
                top: LOGO_SIZE / 2 + 8,
                width: 320,
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              {greeting && (
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: BRAND_GREEN }}>
                  {greeting}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                ロゴにカーソルを合わせるとメニューが開きます
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {items.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ position: 'absolute', bottom: 16, width: '100%', textAlign: 'center' }}
        >
          表示できるメニューがありません
        </Typography>
      )}

      {/* カテゴリ凡例 */}
      {showLegend && (
      <Box sx={{ position: 'absolute', left: 16, bottom: 12, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {Object.entries(MENU_CATEGORIES).map(([id, label]) => (
          <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CATEGORY_COLORS[id] ?? DEFAULT_CATEGORY_COLOR }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{label}</Typography>
          </Box>
        ))}
      </Box>
      )}
    </Box>
  );
}

// -------------------------------------------------------------
// 子メニューカード
// 左右寄りの親は角度方向の外側へ、左右に伸ばす。真上・真下付近の親は外側へ伸ばすと
// 画面端で切れるため、項目の右隣に開く。それでもステージからはみ出す分は内側へ寄せる
// -------------------------------------------------------------
interface ChildFlyoutProps {
  item: OrbitMenuItem;
  angle: number;
  radius: number;
  onNavigate: (path: string) => void;
  onHoverChange: (hovering: boolean) => void;
  // はみ出し補正の基準にするステージ要素
  boundsRef: React.RefObject<HTMLDivElement | null>;
}

// カードとステージ端の最小間隔 (px)
const FLYOUT_EDGE_GAP = 8;

const ChildFlyout: React.FC<ChildFlyoutProps> = ({ item, angle, radius, onNavigate, onHoverChange, boundsRef }) => {
  const color = categoryColorOf(item.menu);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const isVerticalEnd = Math.abs(cos) <= 0.35;

  let left: number;
  let top: number;
  let translateX: string;
  let translateY: string;
  if (isVerticalEnd) {
    // 真上・真下付近 項目の右隣に、項目の高さを中心として開く
    left = cos * radius + ITEM_SIZE / 2 + 14;
    top = sin * radius;
    translateX = '0%';
    translateY = '-50%';
  } else {
    const anchorDistance = radius + ITEM_SIZE / 2 + 18;
    left = cos * anchorDistance;
    top = sin * anchorDistance;
    translateX = cos > 0 ? '0%' : '-100%';
    translateY = sin > 0.35 ? '0%' : sin < -0.35 ? '-100%' : '-50%';
  }

  // 描画後に実寸を測り、ステージからはみ出す分だけ内側へずらす
  const cardRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState({ x: 0, y: 0 });
  useLayoutEffect(() => {
    const card = cardRef.current;
    const bounds = boundsRef.current;
    if (!card || !bounds) return;
    const c = card.getBoundingClientRect();
    const b = bounds.getBoundingClientRect();
    const fit = (start: number, end: number, min: number, max: number) => {
      if (end > max - FLYOUT_EDGE_GAP) return Math.max(max - FLYOUT_EDGE_GAP - end, min + FLYOUT_EDGE_GAP - start);
      if (start < min + FLYOUT_EDGE_GAP) return min + FLYOUT_EDGE_GAP - start;
      return 0;
    };
    setShift({ x: fit(c.left, c.right, b.left, b.right), y: fit(c.top, c.bottom, b.top, b.bottom) });
    // 開いた時点の位置で 1 回だけ補正する (補正後の位置で再計算すると振動するため)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      ref={cardRef}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      sx={{
        position: 'absolute',
        left,
        top,
        transform: `translate(calc(${translateX} + ${shift.x}px), calc(${translateY} + ${shift.y}px))`,
        zIndex: 5,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.85 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
      >
        <Paper
          elevation={6}
          sx={{
            // 幅は中身 (最長のメニュー名) に合わせる。名前は折り返さない
            minWidth: 200,
            width: 'max-content',
            py: 0.75,
            borderRadius: 2,
            borderTop: `3px solid ${color}`,
          }}
        >
          <Typography
            sx={{ px: 1.5, pb: 0.5, fontSize: '0.72rem', fontWeight: 700, color }}
          >
            {item.menu.label}
          </Typography>
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden' }}>
            {item.children.map((child, ci) => (
              <motion.div
                key={child.id || `child-${ci}`}
                initial={{ opacity: 0, x: cos >= 0 ? -8 : 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: ci * 0.03, duration: 0.15 }}
              >
                <FlyoutRow menu={child} color={color} onNavigate={onNavigate} />
                {/* 孫メニュー (案2 では各メニューの子項目) を字下げして並べる */}
                {Array.isArray(child.child) &&
                  child.child.map((grand, gi) => (
                    <FlyoutRow
                      key={grand.id || `grand-${ci}-${gi}`}
                      menu={grand}
                      color={color}
                      onNavigate={onNavigate}
                      nested
                    />
                  ))}
              </motion.div>
            ))}
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

// 子メニューカードの 1 行。nested は孫メニュー (字下げ・小さめ表示)
interface FlyoutRowProps {
  menu: NavigationMenuType;
  color: string;
  onNavigate: (path: string) => void;
  nested?: boolean;
}

const FlyoutRow: React.FC<FlyoutRowProps> = ({ menu, color, onNavigate, nested = false }) => (
  <Box
    role={menu.path ? 'button' : undefined}
    tabIndex={menu.path ? 0 : -1}
    onClick={() => menu.path && onNavigate(menu.path)}
    onKeyDown={(e) => {
      if ((e.key === 'Enter' || e.key === ' ') && menu.path) {
        e.preventDefault();
        onNavigate(menu.path);
      }
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      pl: nested ? 4 : 1.5,
      pr: 1.5,
      py: nested ? 0.4 : 0.6,
      cursor: menu.path ? 'pointer' : 'default',
      color: nested ? 'text.secondary' : 'text.primary',
      transition: 'all 0.15s ease',
      outline: 'none',
      '&:hover, &:focus-visible': menu.path
        ? { bgcolor: `${color}14`, color, transform: 'translateX(3px)' }
        : {},
    }}
  >
    <MenuIcon iconName={menu.icon} fontSize={nested ? 16 : 18} />
    <Typography sx={{ fontSize: nested ? '0.76rem' : '0.82rem', whiteSpace: 'nowrap' }}>
      {menu.label || '無題の子項目'}
    </Typography>
  </Box>
);
